import { useEffect, useRef, useState } from "react";
import { LiaSearchSolid } from "react-icons/lia";
import { MdClose } from "react-icons/md";
import { departmentAPI, inchargeAPI } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import Model from "../../common/Model";
import FormFields from "../../common/FormFields";
import ConfirmDeleteModal from "../../common/DeleteModel";
import ErrorModal from "../../common/CustomModel";
import { HiOutlinePlus } from "react-icons/hi";
import EntityAddModal from "../../common/EntityAddModal";
import Button from "../../common/Button";
import { RegistryHeader } from "../../common/Shared";
import { formatDate } from "../../utlis/formateDate";

const Incharge = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const today = new Date().toISOString()?.split("T")[0];
  const [searchQuery, setSearchQuery] = useState("");
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSubMessage, setErrorSubMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    id: 0,
    incharge_Name: "",
    department_Id: "",
    joining_Date: today,
    leaving_Date: null,
    created_By: userId,
  });
  const [loading, setLoading] = useState(false);
  const [isInchargeData, setIsInchargeData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isDepartmentData, setIsDepartmentData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [modalType, setModalType] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);
  const tableScrollRef = useRef(null);

  const fetchInchargeData = async () => {
    if (isFetchingRef.current) return;
    setLoading(true);
    try {
      const res = await inchargeAPI.getAllIncharge(currentPage, pageSize);
      const newItems = res.data;

      if (currentPage === 1) {
        setIsInchargeData(newItems);
      } else {
        setIsInchargeData((prev) => [...prev, ...newItems]);
      }

      if (newItems.length < pageSize) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      notify("Error fetching location data", "error");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || !hasMore || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;
        setCurrentPage((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, hasMore]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const departmentRes = await departmentAPI.getAll();
      setIsDepartmentData(departmentRes.data || []);
    } catch (error) {
      notify("Error fetching location data", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchInchargeData();
    fetchData();
  }, [currentPage]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredData(isInchargeData);
    } else {
      const filtered = isInchargeData.filter((d) =>
        d.incharge_Name?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredData(filtered);
    }
  }, [searchQuery, isInchargeData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    setIsModelOpen(true);
    setIsEditing(false);
    setFormData({
      id: 0,
      incharge_Name: "",
      department_Id: "",
      joining_Date: today,
      leaving_Date: "",
      created_By: userId,
    });
  };

  const toInputDate = (val) => {
    if (!val) return "";
    return val.split("T")[0];
  };

  const handleEdit = async (id) => {
    try {
      const res = await inchargeAPI.getById(id);
      const d = res.data;
      setFormData({
        id: d.id,
        incharge_Name: d.incharge_Name,
        department_Id: d.department_Id,
        joining_Date: toInputDate(d.joining_Date),
        leaving_Date: toInputDate(d.leaving_Date),
        created_By: userId,
      });
      setIsEditing(true);
      setIsModelOpen(true);
    } catch (error) {
      notify("Failed to fetch details", "error");
    }
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await inchargeAPI.delete(selectedId);
      notify("Deleted successfully", "success");
      fetchInchargeData();
    } catch (error) {
      notify("Failed to delete record", "error");
    }
    setIsDeleteModalOpen(false);
    setSelectedId(null);
  };

  const handleSubmit = async ({ resetOnly = false }) => {
    try {
      const payload = {
        ...formData,
        leaving_Date: formData.leaving_Date ? formData.leaving_Date : null,
      };
      await inchargeAPI.saveOrUpdate(payload);
      notify(
        isEditing ? "Updated successfully" : "Added successfully",
        "success",
      );
      fetchInchargeData();
      setFormData({
        id: 0,
        incharge_Name: "",
        department_Id: "",
        joining_Date: "",
        leaving_Date: null,
        created_By: userId,
      });
      setIsEditing(false);
      if (!resetOnly) {
        setIsModelOpen(false);
      }
    } catch (error) {
      notify("Failed to submit form", "error");
      setErrorMessage("Submission failed");
      setErrorSubMessage("Please check your input or try again.");
      setErrorModalOpen(true);
    }
  };

  const optionMap = {
    unit: {
      setter: setIsDepartmentData,
      fetcher: departmentAPI.getAll,
      mapFn: (data) =>
        data.map((s) => ({
          value: s.department_Id,
          label: s.department_Name,
        })),
      setField: "department_Id",
    },
  };

  const handleEntityAddSuccess = async (type, newOption) => {
    const config = optionMap[type];
    if (!config) return;

    try {
      const updated = await config.fetcher();
      config.setter(updated || []);
      setFormData((prev) => ({
        ...prev,
        [config.setField]: newOption?.value || "",
      }));
      setModalType(null);
    } catch (err) {
      notify("Failed to refresh dropdown", "error");
    }
  };

  const enhancedFields = [
    {
      name: "incharge_Name",
      label: "Incharge Name",
      type: "text",
      required: true,
      errorMessage: "Name is required",
    },
    {
      name: "joining_Date",
      label: "Joining Date",
      type: "date",
      required: true,
      errorMessage: "date is required",
    },
    {
      name: "leaving_Date",
      label: "Leaving Date",
      type: "date",
    },
    {
      name: "department_Id",
      label: "Department Name",
      type: "selectWithAdd",
      options: isDepartmentData.map((s) => ({
        value: s.department_Id,
        label: s.department_Name,
      })),
      onAddNew: () => setModalType("department"),
    },
  ];

  return (
    <>
      <div className="flex flex-col h-full bg-white font-plus">
        <RegistryHeader
          title="Incharge Registry"
          subTitle="Supervisory Role Protocol"
          badge="Personnel Management Control"
        >
          <div className="relative w-full sm:w-80 group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-primary">
              <LiaSearchSolid size={18} className="text-gray-400 transition-colors" />
            </div>
            <input
              type="text"
              placeholder="Search by INCHARGE NAME..."
              className="block w-full pl-12 pr-10 py-3 bg-gray-50 border border-gray-200 text-gray-900 text-[11px] font-black uppercase tracking-[2px] focus:border-primary focus:bg-white focus:outline-none transition-all placeholder:text-gray-300"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-red-500 transition-colors"
              >
                <MdClose size={18} />
              </button>
            )}
          </div>

          <button
            onClick={handleAdd}
            className="bg-primary text-white font-black text-[10px] uppercase tracking-[3px] px-8 py-3 border border-primary hover:bg-[#2c5ebc] transition-all flex items-center gap-3"
          >
            <HiOutlinePlus size={16} /> Add INCHARGE
          </button>
        </RegistryHeader>

        <div className="px-8 pt-0 pb-4 flex-1 overflow-hidden flex flex-col">
          <div
            ref={tableScrollRef}
            className="relative overflow-auto border border-gray-200 bg-white flex-1"
          >
            <div className="min-w-max">
              {loading && <Loader centered />}
              <Table
                columns={[
                  { key: "incharge_Name", label: "Name" },
                  { key: "department_Name", label: "Category Name" },
                  {
                    key: "joining_Date",
                    label: "Joining Date",
                    render: (value) => formatDate(value),
                  },
                  {
                    key: "leaving_Date",
                    label: "Leaving Date",
                    render: (value) => formatDate(value),
                  },
                ]}
                data={filteredData}
                onEdit={(row) => handleEdit(row.id)}
                onDelete={(row) => handleDelete(row.id)}
                tableClassName="text-[12px] font-bold"
              />
            </div>
          </div>
        </div>
      </div>

      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Edit Incharge" : "Add Incharge"}
      >
        <FormFields
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: false });
          }}
          onSubmitAndAddNew={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: true });
          }}
          setIsModelOpen={setIsModelOpen}
          formData={formData}
          handleInputChange={handleInputChange}
          fields={enhancedFields}
          isEditing={isEditing}
          onClear={() => setIsModelOpen(false)}
        />
      </Model>
      <Model
        isOpen={!!modalType}
        onClose={() => setModalType(null)}
        name={`Add ${modalType}`}
      >
        <EntityAddModal
          type={modalType}
          onClose={(newOption) => {
            setModalType(null);
            handleEntityAddSuccess(modalType, newOption);
          }}
        />
      </Model>
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {errorModalOpen && (
        <ErrorModal
          message={errorMessage}
          subMessage={errorSubMessage}
          onClose={() => setErrorModalOpen(false)}
        />
      )}
    </>
  );
};

export default Incharge;
